/* This component only updates its own links. Native product forms own purchasing. */
(() => {
  if (customElements.get('fiveply-pack-switcher')) return;
  const APPROVED_PRODUCTS = ['7647411503188', '9065872064596'];
  const DEFAULT_LABELS = {"heading":"Packaging","one":"1 ball","three":"3-ball pack","shipping":"Shipping options at checkout","ships_us":"Ships from the US","free":"Free shipping · No minimum","states":"48 contiguous US states only","exclusions":"Excludes AK, HI, DC, territories & military addresses","choose":"Choose a color on this product","available":"Available in this pack","sold_out":"This color is sold out in this pack","no_match":"No matching color · View product","per_pack":"/ pack","per_ball":"/ ball","unit":"[price] per ball","open_note":"Choose a pack size to view its product page. Matching colors stay selected.","quantity_note":"For the 3-ball pack, quantity 1 = 3 balls in one color.","shipping_note":"Free shipping applies to the 3-pack’s US warehouse shipment. Other items may have separate shipping charges.","noscript":"After changing color, confirm your color again on the other product page.","updating":"Updating color…","incomplete":"Color update incomplete · Choose color on product page","incomplete_announcement":"Color update did not finish. Open a product page to choose your color."};

  class FiveplyPackSwitcher extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.productInfo = this.closest('product-info[id^="MainProduct-"]');
      if (!this.productInfo || !APPROVED_PRODUCTS.includes(this.dataset.productId)) return;
      if (this.closest('quick-add-modal') || this.productInfo.dataset.originalSection) return;
      if (!this.loadCatalog(this.querySelector('[data-fiveply-data]'))) return;
      this.initialized = true;
      this.previewParams = new URLSearchParams();
      const pageParams = new URLSearchParams(window.location.search);
      ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
        if (pageParams.has(key)) this.previewParams.set(key, pageParams.get(key));
      });
      this.handleChange = this.handleChange.bind(this);
      this.productInfo.addEventListener('change', this.handleChange);
      this.handleClick = (event) => {
        if (this.pending && event.target.closest('[data-fiveply-card]')) event.preventDefault();
      };
      this.addEventListener('click', this.handleClick);
      this.addEventListener('auxclick', this.handleClick);

      // Dawn replaces variant-selects after every selection. Subscribe to the
      // section-scoped result and delegate DOM events from the persistent parent.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== this.dataset.sectionId) return;
          const freshComponent = Array.from(data.html?.querySelectorAll('fiveply-pack-switcher') || [])
            .find((node) => node.dataset.productId === this.dataset.productId && node.dataset.sectionId === this.dataset.sectionId);
          if (freshComponent) this.loadCatalog(freshComponent.querySelector('[data-fiveply-data]'));
          this.render(data.variant?.id, true);
        });
      }
      this.render(this.dataset.currentVariantId);
    }

    disconnectedCallback() {
      window.clearTimeout(this.pendingTimeout);
      this.productInfo?.removeEventListener('change', this.handleChange);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('auxclick', this.handleClick);
      this.unsubscribeVariant?.();
      this.initialized = false;
    }

    loadCatalog(script) {
      try {
        const catalog = JSON.parse(script?.textContent || '');
        const products = catalog.products;
        this.labels = {...DEFAULT_LABELS, ...catalog.i18n};
        if (!Array.isArray(products) || products.length !== 2) return false;
        if (APPROVED_PRODUCTS.some((id) => products.filter((product) => String(product.id) === id).length !== 1)) return false;
        if (products.some((product) => !Array.isArray(product.variants) || !product.url)) return false;
        this.products = products;
        return true;
      } catch {
        return false;
      }
    }

    handleChange(event) {
      if (event.target.closest('variant-selects')) {
        window.clearTimeout(this.pendingTimeout);
        this.pending = true;
        this.setAttribute('aria-busy', 'true');
        this.querySelectorAll('[data-fiveply-card]').forEach((card) => {
          card.removeAttribute('href');
          card.setAttribute('aria-disabled', 'true');
          card.querySelector('[data-fiveply-status]').textContent = this.labels.updating;
        });
        // Native theme request failures have no public error event. Restore safe
        // product links, without an unconfirmed color, if the request stalls.
        this.pendingTimeout = window.setTimeout(() => {
          if (!this.isConnected || !this.pending) return;
          this.render(null);
          this.querySelectorAll('[data-fiveply-status]').forEach((status) => {
            status.textContent = this.labels.incomplete;
          });
          this.querySelector('[data-fiveply-announcement]').textContent = this.labels.incomplete_announcement;
        }, 12000);
        return;
      }
      const form = event.target.closest('form');
      if (event.target.name === 'id' && form?.id === `product-form-${this.dataset.sectionId}`) {
        this.render(event.target.value, true);
      }
    }

    colorKey(color) {
      const text = String(color || '').trim().toLowerCase();
      const number = text.match(/^(\d+)\s*#/);
      return number ? `number:${Number(number[1])}` : `name:${text}`;
    }

    matchingVariant(product, currentVariant) {
      if (!currentVariant) return null;
      const exact = product.variants.filter((variant) => String(variant.color).trim().toLowerCase() === String(currentVariant.color).trim().toLowerCase());
      if (exact.length === 1) return exact[0];
      const matches = product.variants.filter((variant) => this.colorKey(variant.color) === this.colorKey(currentVariant.color));
      return matches.length === 1 ? matches[0] : null;
    }

    productUrl(product, variant) {
      const url = new URL(product.url, window.location.origin);
      const localeRoot = (this.dataset.localeRoot || '/').replace(/\/$/, '');
      if (localeRoot && url.pathname.startsWith('/products/')) url.pathname = `${localeRoot}${url.pathname}`;
      url.search = '';
      if (variant) url.searchParams.set('variant', String(variant.id));
      this.previewParams.forEach((value, key) => url.searchParams.set(key, value));
      return url.pathname + url.search;
    }

    render(variantId, announce = false) {
      window.clearTimeout(this.pendingTimeout);
      this.pending = false;
      this.removeAttribute('aria-busy');
      const source = this.products.find((product) => String(product.id) === this.dataset.productId);
      const currentVariant = source?.variants.find((variant) => String(variant.id) === String(variantId));
      const statuses = [];
      this.products.forEach((product) => {
        const card = this.querySelector(`[data-fiveply-card="${product.id}"]`);
        if (!card) return;
        const variant = this.matchingVariant(product, currentVariant);
        card.href = this.productUrl(product, variant);
        card.removeAttribute('aria-disabled');
        card.querySelector('[data-fiveply-price]').textContent = variant
          ? `${variant.price} ${product.balls === 3 ? this.labels.per_pack : this.labels.per_ball}`
          : this.labels.choose;
        card.querySelector('[data-fiveply-unit]').textContent = variant && product.balls === 3
          ? this.labels.unit.replace('[price]', variant.unitPrice) : '';
        const status = card.querySelector('[data-fiveply-status]');
        status.textContent = !variant ? this.labels.no_match
          : variant.available ? this.labels.available : this.labels.sold_out;
        status.classList.toggle('fiveply-pack-switcher__status--sold-out', Boolean(variant && !variant.available));
        card.setAttribute('aria-label', `${product.balls === 3 ? this.labels.three : this.labels.one}${variant ? `, ${variant.color}, ${variant.price}` : ''}. ${status.textContent}.`);
        statuses.push(`${product.balls === 3 ? this.labels.three : this.labels.one}: ${status.textContent}.`);
      });
      if (announce) this.querySelector('[data-fiveply-announcement]').textContent = statuses.join(' ');
    }
  }

  customElements.define('fiveply-pack-switcher', FiveplyPackSwitcher);
})();
